import { Box, Typography, Menu, MenuItem, Dialog } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import { useState, useEffect, useMemo } from 'react';
import { API } from '../../../../../../api';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../../../redux/hooks';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import InfoTip from '../../../../../../components/ui/InfoTip';
import { Bone } from '../../../../../../components/ui/DashboardSkeletons';
import useAdminPrograms, { EMPTY_ADMIN_PROGRAMS } from '../../../../../../queries/useAdminPrograms';
import { useMemberWord } from '../../../../../../utils/memberTypeLabels';
import { scoreOrDash } from '../../../../../../utils/requirementsScore';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

/* ── Design tokens ── */
const BG_PAGE = '#0f0f0f';
const BG_CARD = '#1c1c1c';
const BG_CARD_CONTRAST = '#151515';
const BG_CARD_HIGHLIGHT = '#232323';
const BG_BUTTON_BASIC = '#1c1c1c';
const BG_HOVER_ROW = 'rgba(255,255,255,0.05)';
const BAR_FILL = 'rgba(255,255,255,0.1)';
const TEXT_PRIMARY = '#fdfdfd';
const TEXT_SECONDARY = '#b6b6b6';
const TEXT_TERTIARY = '#868686';
const TEXT_QUATERNARY = '#565656';
const ACCENT = '#f79420';
const ACCENT_DARK = '#f15a26';
const BORDER = '#2a2a2a';
const SUCCESS = '#22c55e';
const ERROR = '#ff6a55';
const WARNING = '#facc15';
const GREEN_TINT = 'rgba(34,197,94,0.2)';
/* The Capability release's frames and header fill (see Insights' tables). */
const ACCENT_LINE = 'rgba(247, 148, 32, 0.45)';
const ACCENT_TINT = 'rgba(247, 148, 32, 0.06)';
const HEAD_FILL = '#232323';
const CARD_FRAME = `1px solid ${BORDER}`;

/* ── Program type sets ── */
const MASTERCLASS_TYPES = new Set(['masterclass', 'masterclass&mentorship', 'guided-masterclass']);
const MENTORSHIP_TYPES = new Set(['mentorship']);

/* Sentinel id for the aggregated "All Programs" rollup */
const ALL_PROGRAMS_ID = '__ALL_PROGRAMS__';
const ALL_PROGRAMS_NAME = 'All Programs';

/* How many per-program insight requests the rollup keeps in flight at once. */
const ROLLUP_CONCURRENCY = 4;

/* Programs that were never published (still in design) carry the fixed build
   saving without anyone having taken them, so the rollup leaves them out
   (product decision, Sep 2026). Status vocabulary is the dashboard's:
   design/designed = draft; published, invited, active(d) = live. Unknown but
   non-empty statuses count as live so a legacy program is never silently
   dropped. */
const isPublishedProgram = (status?: string) => {
  const s = (status || '').toLowerCase();
  return s !== '' && s !== 'design' && s !== 'designed' && s !== 'not-started';
};

/* Promise.all with a cap on how many `fn` calls run at once; results keep the
   input order. */
const mapWithConcurrency = async <T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

type InsightsVariant = 'masterclass' | 'mentorship' | null;

const getInsightsVariant = (type?: string): InsightsVariant => {
  if (!type) return null;
  if (MASTERCLASS_TYPES.has(type)) return 'masterclass';
  if (MENTORSHIP_TYPES.has(type)) return 'mentorship';
  return null;
};

/* ── Masterclass BE response shape ── */
interface AssessmentMember {
  userId: string;
  name: string;
  headshot?: string;
  result: 'correct' | 'incorrect' | 'notAttempted';
}

interface FunnelMember {
  userId: string;
  name: string;
  headshot?: string;
  stage: 'invited' | 'accepted' | 'onboarded' | 'inProgress' | 'completed';
  progressScore: number;
  /** Null for someone no quiz in the program assesses (Audience Requirements, DA-5056 R5). */
  mcqScore: number | null;
  isAligned: boolean;
}

interface InsightsV3 {
  programId: string;
  programName: string;
  currency: string;
  totalMembers: number;
  totalValueAdded: {
    totalAmount: number;
    totalHoursSaved: number;
  };
  timeSpent: {
    totalHours: number;
    rows: Array<{ channel: string; hours: number; percentage: number; scopedPerProgram?: boolean }>;
  };
  averageScores: {
    averageProgressScore: number;
    averageAssessmentScore: number;
    scope?: { onboardedMembers: number };
  };
  assessmentAnalysis: Array<{
    questionId: string;
    questionText: string;
    averageCorrectPct: number;
    sampleSize?: number;
    members?: AssessmentMember[];
  }>;
  programFunnel: {
    invited: { count: number; percentage: number };
    accepted: { count: number; percentage: number };
    onboarded: { count: number; percentage: number };
    inProgress: { count: number; percentage: number };
    completed: { count: number; percentage: number };
    membersAligned: { count: number; percentage: number };
    members?: FunnelMember[];
  };
  leaderboard: Array<{
    userId: string;
    name: string;
    headshot?: string;
    progressScore: number;
    /** Null for someone no quiz in the program assesses (DA-5056 R5): shown "-". */
    mcqScore: number | null;
  }>;
}

/* ── Mentorship BE response shape ── */
interface MentorshipFunnelMember {
  userId: string;
  name: string;
  headshot?: string;
}

interface MentorshipInsightsV3 {
  programId: string;
  programName: string;
  currency: string;
  totalMembers: number;
  totalValueAdded: {
    totalAmount: number;
    totalHoursSaved: number;
  };
  timeSpent: {
    totalHours: number;
    rows: Array<{ channel: string; hours: number; percentage: number }>;
  };
  membersEngagement: {
    messagesShared: number;
    meetingsScheduled: number;
    averageRating: number;
  };
  objectiveAnalysis: Array<{
    objectiveText: string;
    totalCount: number;
    learningObjectiveCount: number;
    areaOfExpertiseCount: number;
  }>;
  programFunnel: {
    totalMentees: number;
    invited: { count: number; percentage: number };
    accepted: { count: number; percentage: number };
    onboarded: { count: number; percentage: number };
    engaged: { count: number; percentage: number };
    objectivesInProgress: { count: number; percentage: number };
    objectivesCompleted: { count: number; percentage: number };
    members?: MentorshipFunnelMember[];
  };
  /** Parallel mentor funnel for the Mentees/Mentors toggle (optional — older BE omits it). */
  mentorFunnel?: {
    totalMentors: number;
    invited: { count: number; percentage: number };
    accepted: { count: number; percentage: number };
    onboarded: { count: number; percentage: number };
    engaged: { count: number; percentage: number };
    objectivesInProgress: { count: number; percentage: number };
    objectivesCompleted: { count: number; percentage: number };
    members?: MentorshipFunnelMember[];
  };
  leaderboard: Array<{
    userId: string;
    name: string;
    headshot?: string;
    messages: number;
    meetings: number;
  }>;
}

/* ── Helpers ── */
const scoreColor = (score: number) => {
  if (score >= 80) return SUCCESS;
  if (score >= 60) return WARNING;
  return ERROR;
};

const STAGE_LABELS: Record<FunnelMember['stage'], string> = {
  invited: 'Invited',
  accepted: 'Accepted',
  onboarded: 'Onboarded',
  inProgress: 'In progress',
  completed: 'Completed'
};

const stageColor = (stage: FunnelMember['stage']) => {
  if (stage === 'completed') return SUCCESS;
  if (stage === 'inProgress') return WARNING;
  if (stage === 'onboarded') return ACCENT;
  return TEXT_TERTIARY;
};

const channelLabel = (c: string) =>
  c === 'copilot' ? 'Elliott' : c.charAt(0).toUpperCase() + c.slice(1);

const downloadCsv = (rows: string[][], programName: string) => {
  const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${programName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-insights.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ── Shared: a figure or list still loading: a dark placeholder in its place ── */
const LoadingFigure = ({ width = 72, height = 22 }: { width?: number; height?: number }) => (
  <Bone width={width} height={height} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
);
const LoadingRows = ({ rows = 4 }: { rows?: number }) => (
  <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '10px' }}>
    {Array.from({ length: rows }, (_, i) => (
      <Bone key={i} height={20} />
    ))}
  </Box>
);

// Phone: a card sits at its own height, so the space its parts were spread over becomes a gap.
const PHONE_CARD = { gap: '20px', justifyContent: 'flex-start' };

/* ── Shared: Card title with info tooltip ── */
const CardTitle = ({ title, sub, tooltip }: { title: string; sub?: string; tooltip?: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          color: TEXT_PRIMARY,
          whiteSpace: 'nowrap',
          lineHeight: 1
        }}
      >
        {title}
      </Typography>
      <InfoTip title={tooltip ?? sub ?? ''} />
    </Box>
    {sub && (
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 400, color: TEXT_QUATERNARY, lineHeight: 1, width: '100%' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

/* ── Shared: Funnel stat bar (hover-reveals count/%) ── */
const StatBar = ({
  label,
  count,
  percentage,
  loading
}: {
  label: string;
  count: number;
  percentage: number;
  loading: boolean;
}) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '5px',
      p: '10px',
      overflow: 'hidden',
      flex: 1,
      '&:hover .barFill': { opacity: 1 }
    }}
  >
    <Box className="barFill" sx={{ position: 'absolute', inset: 0, background: BAR_FILL, width: '100%', opacity: 0, transition: 'opacity 0.15s' }} />
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, position: 'relative', lineHeight: 1 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '5px', position: 'relative', minWidth: 80, justifyContent: 'flex-start' }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_PRIMARY, textTransform: 'uppercase', lineHeight: 0.85 }}>
        {loading ? <LoadingFigure /> : count}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 0.85 }}>
        {loading ? '' : `${Math.round(percentage)}%`}
      </Typography>
    </Box>
  </Box>
);

/* ── Shared: Total Value Added ── */
const CardTotalValueAdded = ({
  totalAmount,
  hoursSaved,
  currency,
  loading
}: {
  totalAmount: number;
  hoursSaved: number;
  currency: string;
  loading: boolean;
}) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', [MOBILE_SX]: PHONE_CARD }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          color: TEXT_PRIMARY,
          whiteSpace: 'nowrap',
          lineHeight: 1
        }}
      >
        Total value added
      </Typography>
      <InfoTip title="The financial value of the time saved creating and delivering your program on Dosen versus alternatives" />
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 48, fontWeight: 600, color: ACCENT, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {loading ? (
          <LoadingFigure width={160} height={44} />
        ) : (
          `${currency}${Math.round(totalAmount).toLocaleString('en-US')}`
        )}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_QUATERNARY, lineHeight: 1.2 }}>
          {loading ? <LoadingFigure /> : Math.round(hoursSaved).toLocaleString('en-US')}
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_QUATERNARY, lineHeight: 1.2 }}>
          Hours saved
        </Typography>
      </Box>
    </Box>
  </Box>
);

/* ── Shared: Time Spent ── */
/**
 * Each row's share of the hours, in whole percent, with largest-remainder rounding so the rows sum to
 * 100. The Time Spent card and both CSV exports read it, so the file says what the screen says.
 */
const sharesOf = (rows: Array<{ hours: number }>): number[] => {
  const sum = rows.reduce((acc, r) => acc + (r.hours || 0), 0);
  if (sum <= 0) return rows.map(() => 0);
  const raw = rows.map((r) => ((r.hours || 0) / sum) * 100);
  const result = raw.map((v) => Math.floor(v));
  const order = raw.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
  let remainder = 100 - result.reduce((acc, v) => acc + v, 0);
  for (let k = 0; remainder > 0 && order.length > 0; k++, remainder--) {
    result[order[k % order.length].i] += 1;
  }
  return result;
};

const CardTimeSpent = ({ data, loading }: { data: InsightsV3['timeSpent'] | null; loading: boolean }) => {
  const rows = data?.rows ?? [
    { channel: 'copilot', hours: 0, percentage: 0 },
    { channel: 'lessons', hours: 0, percentage: 0 },
    { channel: 'resources', hours: 0, percentage: 0 }
  ];
  const total = data?.totalHours ?? 0;
  const formatHrs = (h: number) => h.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // Recompute percentages from hours using largest-remainder rounding to guarantee rows sum to 100%
  const displayPcts = sharesOf(rows);

  return (
    <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%' }}>
      <CardTitle title="Time spent" sub="Learning / Accessing Knowledge" />
      <Box sx={{ display: 'flex', gap: '10px', flex: 1, minHeight: 0, [MOBILE_SX]: { flexDirection: 'column' } }}>
        <Box sx={{ background: BG_CARD_CONTRAST, borderRadius: '5px', p: '10px', minWidth: 114, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1 }}>Total</Typography>
          <Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, color: ACCENT, lineHeight: 1 }}>
              {loading ? <LoadingFigure /> : formatHrs(total)}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: ACCENT, lineHeight: 1.1 }}>
              hrs.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, justifyContent: 'center' }}>
          {rows.map((row, idx) => (
            <Box
              key={row.channel}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '5px',
                p: '10px',
                overflow: 'hidden',
                flex: 1,
                '&:hover .barFill': { opacity: 1 }
              }}
            >
              <Box className="barFill" sx={{ position: 'absolute', inset: 0, background: BAR_FILL, width: '100%', borderRadius: '5px', opacity: 0, transition: 'opacity 0.15s' }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_SECONDARY, position: 'relative', whiteSpace: 'nowrap', lineHeight: 1 }}>
                {channelLabel(row.channel)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '5px', position: 'relative', minWidth: 148, justifyContent: 'flex-start' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_PRIMARY, textTransform: 'uppercase', lineHeight: 0.85 }}>
                  {loading ? <LoadingFigure /> : formatHrs(row.hours)}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 0.85 }}>
                  hrs.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 0.85 }}>
                  {loading ? '' : `${displayPcts[idx]}%`}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════
   MASTERCLASS CARDS
══════════════════════════════════════════ */

const Score = ({ label, value, loading }: { label: string; value: number; loading: boolean }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 1.2, whiteSpace: 'pre-line' }}>
      {label.replace(' Score', '\nScore')}
    </Typography>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 54, fontWeight: 500, color: scoreColor(value), lineHeight: 1 }}>
      {loading ? <LoadingFigure /> : `${Math.round(value)}%`}
    </Typography>
  </Box>
);

const CardAverageScores = ({ data, loading }: { data: InsightsV3['averageScores'] | null; loading: boolean }) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', [MOBILE_SX]: PHONE_CARD }}>
    <CardTitle title="Average scores" sub="Average scores across all onboarded users" />
    <Box sx={{ display: 'flex', gap: '20px', width: '100%' }}>
      <Score label="Average Progress Score" value={data?.averageProgressScore ?? 0} loading={loading} />
      <Score label="Average Assessment Score" value={data?.averageAssessmentScore ?? 0} loading={loading} />
    </Box>
  </Box>
);

const ResultIcon = ({ result }: { result: AssessmentMember['result'] }) => {
  if (result === 'correct') return <CheckIcon sx={{ fontSize: 18, color: SUCCESS, flexShrink: 0 }} />;
  if (result === 'incorrect') return <CloseIcon sx={{ fontSize: 18, color: ERROR, flexShrink: 0 }} />;
  return <RemoveIcon sx={{ fontSize: 18, color: TEXT_QUATERNARY, flexShrink: 0 }} />;
};

const CardAssessmentAnalysis = ({ items, loading }: { items: InsightsV3['assessmentAnalysis']; loading: boolean }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%', overflow: 'hidden' }}>
      <CardTitle title="Assessment analysis" sub="Scoring average for each assessment question" />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', overflowY: 'auto', minHeight: 0 }}>
        {loading && <LoadingRows />}
        {!loading && items.length === 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_TERTIARY, p: '10px' }}>No assessment data yet</Typography>
        )}
        {!loading && items.map((item) => {
          const expanded = expandedId === item.questionId;
          const members = item.members ?? [];
          return (
            <Box
              key={item.questionId}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '5px',
                overflow: 'hidden',
                flexShrink: 0,
                background: expanded ? BG_CARD_CONTRAST : 'transparent'
              }}
            >
              <Box
                onClick={() => setExpandedId(expanded ? null : item.questionId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  p: '10px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  '&:hover': { background: BG_HOVER_ROW }
                }}
              >
                <DosenTooltip title={item.questionText} placement="top">
                  <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 500, color: TEXT_SECONDARY, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' } }}>
                    {item.questionText}
                  </Typography>
                </DosenTooltip>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 20, fontWeight: 600, color: TEXT_TERTIARY, whiteSpace: 'nowrap', lineHeight: 1 }}>
                  {`${Math.round(item.averageCorrectPct)}%`}
                </Typography>
                <PlaylistAddCheckIcon sx={{ fontSize: 24, color: expanded ? ACCENT : TEXT_TERTIARY, flexShrink: 0, transition: 'color 0.15s' }} />
              </Box>
              {expanded && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: '10px', borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', lineHeight: 'normal' }}>
                      Name
                    </Typography>
                  </Box>
                  {members.length === 0 && (
                      <Typography
                        sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_TERTIARY, p: '10px' }}
                      >
                        No responses yet
                      </Typography>
                  )}
                  {members.map((m) => (
                    <Box
                      key={m.userId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        p: '10px',
                        background: m.result === 'correct' ? GREEN_TINT : 'transparent'
                      }}
                    >
                      <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: m.result === 'notAttempted' ? TEXT_TERTIARY : TEXT_PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' } }}>
                        {m.name}
                      </Typography>
                      <ResultIcon result={m.result} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ProgramMembersDialog = ({
  open,
  onClose,
  members
}: {
  open: boolean;
  onClose: () => void;
  members: FunnelMember[];
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: '6px', backgroundImage: 'none' } }}
  >
    <Box sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '70vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}
        >
          Learners
        </Typography>
        <CloseIcon onClick={onClose} sx={{ fontSize: 22, color: TEXT_TERTIARY, cursor: 'pointer', '&:hover': { color: TEXT_PRIMARY } }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
        {members.length === 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_TERTIARY, p: '10px' }}>
            No people yet
          </Typography>
        )}
        {members.map((m) => (
          <Box
            key={m.userId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              px: '8px',
              py: '10px',
              borderRadius: '6px',
              '&:hover': { background: BG_HOVER_ROW }
            }}
          >
            <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible', lineHeight: 1.25 } }}>
              {m.name}
            </Typography>
            <Box sx={{ flexShrink: 0, px: '10px', py: '4px', borderRadius: '999px', border: `1px solid ${stageColor(m.stage)}` }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: stageColor(m.stage), lineHeight: 1, whiteSpace: 'nowrap' }}>
                {STAGE_LABELS[m.stage]}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Dialog>
);

const CardProgramFunnel = ({
  funnel,
  totalMembers,
  loading
}: {
  funnel: InsightsV3['programFunnel'] | null;
  totalMembers: number;
  loading: boolean;
}) => {
  type StageKey = 'invited' | 'accepted' | 'onboarded' | 'inProgress' | 'completed';
  const stages: Array<{ label: string; key: StageKey }> = [
    { label: 'Invited', key: 'invited' },
    { label: 'Accepted', key: 'accepted' },
    { label: 'Onboarded', key: 'onboarded' },
    { label: 'In progress', key: 'inProgress' },
    { label: 'Completed', key: 'completed' }
  ];
  const [membersOpen, setMembersOpen] = useState(false);
  const members = funnel?.members ?? [];

  return (
    <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', overflow: 'hidden' }}>
      <CardTitle title="Program funnel" tooltip="A snapshot of your program members' onboarding" />
      <Box sx={{ background: BG_CARD_HIGHLIGHT, borderRadius: '5px', p: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 32, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}>
            {loading ? <LoadingFigure /> : totalMembers}
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 1 }}>
            Learners
          </Typography>
        </Box>
        <Typography onClick={() => setMembersOpen(true)} sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: ACCENT, cursor: 'pointer', lineHeight: 1, '&:hover': { textDecoration: 'underline' } }}>
          View
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minHeight: 0, justifyContent: 'center', my: '12px' }}>
        {stages.map((s) => (
          <StatBar key={s.key} label={s.label} count={funnel?.[s.key]?.count ?? 0} percentage={funnel?.[s.key]?.percentage ?? 0} loading={loading} />
        ))}
      </Box>
      <Box sx={{ background: BG_CARD_CONTRAST, borderRadius: '5px', p: '10px', display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 32, fontWeight: 600, color: ACCENT, lineHeight: 1 }}>
          {loading ? <LoadingFigure /> : funnel?.membersAligned.count ?? 0}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}>
            People aligned:
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: ACCENT, lineHeight: 1 }}>
            {loading ? <LoadingFigure /> : `${Math.round(funnel?.membersAligned.percentage ?? 0)}%`}
          </Typography>
        </Box>
      </Box>
      <ProgramMembersDialog open={membersOpen} onClose={() => setMembersOpen(false)} members={members} />
    </Box>
  );
};

const CardMembersLeaderboard = ({ members, loading }: { members: InsightsV3['leaderboard']; loading: boolean }) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%', overflow: 'hidden' }}>
    <CardTitle title="Leaderboard" />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', p: '9px 8px', background: HEAD_FILL, borderBottom: `1px solid ${BORDER}`, borderRadius: '4px 4px 0 0' }}>
        <Typography sx={{ flex: 1, pl: '8px', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', lineHeight: 1 }}>Name</Typography>
        <Typography sx={{ width: 72, fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Progress</Typography>
        <Typography sx={{ width: 60, fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Quiz %</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && <LoadingRows />}
        {!loading && members.length === 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: TEXT_SECONDARY, p: '10px' }}>
            No people yet
          </Typography>
        )}
        {!loading && members.map((m) => (
          <Box key={m.userId} sx={{ display: 'flex', gap: '15px', alignItems: 'center', px: '8px', py: '10px', borderRadius: '6px', '&:hover': { background: BG_HOVER_ROW } }}>
            <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', [MOBILE_SX]: { display: 'block', WebkitLineClamp: 'unset', overflow: 'visible' } }}>
              {m.name}
            </Typography>
            <Typography sx={{ width: 72, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
              {`${Math.round(m.progressScore)}%`}
            </Typography>
            <Typography sx={{ width: 60, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
              {scoreOrDash(m.mcqScore, (n) => `${Math.round(n)}%`)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/* ══════════════════════════════════════════
   ALL PROGRAMS ROLLUP — programs breakdown
══════════════════════════════════════════ */

interface ProgramBreakdownRow {
  grpId: string;
  name: string;
  typeLabel: string;
  members: number;
  valueAdded: number;
  hasInsights: boolean;
  /** Why this program's insights are missing: the admin can't read them (a group
      admin of other programs) or the request failed. Left out of every total. */
  unavailable?: 'noAccess' | 'failed';
}

/* Replaces Assessment Analysis in the aggregated view: every program with its
   headline numbers, click a row to drill into that program's insights. */
const CardProgramsBreakdown = ({
  rows,
  currency,
  loading,
  onSelect
}: {
  rows: ProgramBreakdownRow[];
  currency: string;
  loading: boolean;
  onSelect: (grpId: string) => void;
}) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%', overflow: 'hidden' }}>
    <CardTitle
      title="Programs"
      sub="Value added and people by program"
      tooltip="Every published program in your organization (drafts are left out). Select a program to open its insights."
    />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', p: '9px 8px', background: HEAD_FILL, borderBottom: `1px solid ${BORDER}`, borderRadius: '4px 4px 0 0' }}>
        <Typography sx={{ flex: 1, pl: '8px', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', lineHeight: 1 }}>Program</Typography>
        <Typography
          sx={{
            width: 76,
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            fontWeight: 600,
            color: TEXT_TERTIARY,
            textTransform: 'uppercase',
            textAlign: 'right',
            lineHeight: 1
          }}
        >
          People
        </Typography>
        <Typography sx={{ width: 100, fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Value Added</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && <LoadingRows />}
        {!loading && rows.length === 0 && <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: TEXT_SECONDARY, p: '10px' }}>No published programs yet</Typography>}
        {!loading && rows.map((r) => (
          <Box
            key={r.grpId}
            onClick={() => onSelect(r.grpId)}
            sx={{ display: 'flex', gap: '15px', alignItems: 'center', px: '8px', py: '10px', borderRadius: '6px', cursor: 'pointer', '&:hover': { background: BG_HOVER_ROW } }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 15,
                  fontWeight: 600,
                  color: TEXT_SECONDARY,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  // Phone: the whole name, on as many lines as it takes.
                  [MOBILE_SX]: { whiteSpace: 'normal', overflowWrap: 'break-word' }
                }}
              >
                {r.name}
              </Typography>
              {r.typeLabel && (
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_QUATERNARY, lineHeight: 1.3, mt: '2px' }}>
                  {r.typeLabel}
                </Typography>
              )}
            </Box>
            <Typography sx={{ width: 76, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
                {r.unavailable ? '' : r.members}
            </Typography>
            <Typography sx={{ width: 100, fontFamily: 'Inter, sans-serif', fontSize: r.unavailable ? 13 : 16, fontWeight: 600, color: r.unavailable ? TEXT_QUATERNARY : TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
              {r.unavailable ? (r.unavailable === 'noAccess' ? 'No access' : 'Unavailable') : `${currency}${Math.round(r.valueAdded).toLocaleString('en-US')}`}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* Programs the admin can't read (or that failed to load) are named in the
          list but excluded from every total — say so rather than showing $0. */}
      {!loading && rows.some((r) => r.unavailable) && (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_QUATERNARY, pt: '10px', px: '8px' }}>
          {rows.filter((r) => r.unavailable).length === 1
            ? '1 program could not be loaded and is left out of the totals.'
            : `${rows.filter((r) => r.unavailable).length} programs could not be loaded and are left out of the totals.`}
        </Typography>
      )}
    </Box>
  </Box>
);

/* Aggregate every published program's insights into a single masterclass-shaped
   rollup (drafts are filtered out before this runs). Value Added and Time Spent
   draw on every program type; the score-based cards (Average Scores, Program
   Funnel, Leaderboard) are masterclass-weighted — mentorship programs contribute
   only their value/time. People are counted once across programs. */
interface AggregateResult {
  program: { grpId: string; name: string; typeLabel: string };
  variant: InsightsVariant;
  masterclass: InsightsV3 | null;
  mentorship: MentorshipInsightsV3 | null;
  /** Set when the insights request failed; the program is listed but not totalled. */
  error?: 'noAccess' | 'failed';
}

const buildAllProgramsAggregate = (
  results: AggregateResult[],
  currency: string
): { insights: InsightsV3; breakdown: ProgramBreakdownRow[] } => {
  let totalAmount = 0;
  let totalHoursSaved = 0;

  const channelOrder: string[] = [];
  const channelHours: Record<string, number> = {};

  let progressWeighted = 0;
  let assessmentWeighted = 0;
  let scoreWeight = 0;
  let progressPlain = 0;
  let assessmentPlain = 0;
  let masterclassCount = 0;

  // Members are counted ONCE across programs (product decision, Sep 2026): the
  // headline count and the funnel come from the deduplicated member list below,
  // not from adding up per-program counts. Pending invites carry no user id yet,
  // so they are the one stage still counted per invite.
  let pendingInvites = 0;
  const funnelMembers: FunnelMember[] = [];
  const leaderboard: InsightsV3['leaderboard'] = [];
  const breakdown: ProgramBreakdownRow[] = [];

  results.forEach(({ program, variant, masterclass, mentorship, error }) => {
    const payload = masterclass ?? mentorship;

    // Value Added + Time Spent — all program types contribute
    if (payload) {
      totalAmount += payload.totalValueAdded?.totalAmount ?? 0;
      totalHoursSaved += payload.totalValueAdded?.totalHoursSaved ?? 0;
      (payload.timeSpent?.rows ?? []).forEach((r) => {
        if (!(r.channel in channelHours)) {
          channelHours[r.channel] = 0;
          channelOrder.push(r.channel);
        }
        channelHours[r.channel] += r.hours ?? 0;
      });
    }

    let members = 0;
    if (variant === 'masterclass' && masterclass) {
      masterclassCount += 1;
      members = masterclass.totalMembers ?? 0;

      const w = masterclass.averageScores?.scope?.onboardedMembers ?? masterclass.programFunnel?.onboarded?.count ?? 0;
      progressWeighted += (masterclass.averageScores?.averageProgressScore ?? 0) * w;
      assessmentWeighted += (masterclass.averageScores?.averageAssessmentScore ?? 0) * w;
      scoreWeight += w;
      progressPlain += masterclass.averageScores?.averageProgressScore ?? 0;
      assessmentPlain += masterclass.averageScores?.averageAssessmentScore ?? 0;

      pendingInvites += masterclass.programFunnel?.invited?.count ?? 0;
      if (masterclass.programFunnel?.members) funnelMembers.push(...masterclass.programFunnel.members);
      if (masterclass.leaderboard) leaderboard.push(...masterclass.leaderboard);
    } else if (variant === 'mentorship' && mentorship) {
      members = mentorship.programFunnel?.totalMentees ?? 0;
    }

    breakdown.push({
      grpId: program.grpId,
      name: program.name,
      typeLabel: program.typeLabel,
      members,
      valueAdded: payload?.totalValueAdded?.totalAmount ?? 0,
      hasInsights: Boolean(payload),
      unavailable: error
    });
  });

  // Sums of floats: rounded once here (hours to a tenth, shares and averages to whole percent), so the
  // cards and the CSV read the same numbers and the file carries no float noise (77.77777777777779).
  const tenth = (n: number) => Math.round(n * 10) / 10;
  const timeTotalHours = tenth(channelOrder.reduce((acc, c) => acc + channelHours[c], 0));
  const timeShares = sharesOf(channelOrder.map((c) => ({ hours: channelHours[c] })));
  const timeRows = channelOrder.map((channel, i) => ({
    channel,
    hours: tenth(channelHours[channel]),
    percentage: timeShares[i]
  }));

  const averageProgressScore = Math.round(
    scoreWeight > 0 ? progressWeighted / scoreWeight : masterclassCount > 0 ? progressPlain / masterclassCount : 0
  );
  const averageAssessmentScore = Math.round(
    scoreWeight > 0 ? assessmentWeighted / scoreWeight : masterclassCount > 0 ? assessmentPlain / masterclassCount : 0
  );

  // A member can sit in several programs — collapse to one row each so keys stay
  // unique. Leaderboard keeps the member's strongest scores; the funnel keeps the
  // furthest stage they reached.
  // A quiz score of null (nothing assesses them there, DA-5056 R5) ranks below any score.
  const quizRank = (score: number | null) => (score === null ? -1 : score);
  const lbMap = new Map<string, InsightsV3['leaderboard'][number]>();
  leaderboard.forEach((m) => {
    const prev = lbMap.get(m.userId);
    if (
      !prev ||
      m.progressScore > prev.progressScore ||
      (m.progressScore === prev.progressScore && quizRank(m.mcqScore) > quizRank(prev.mcqScore))
    ) {
      lbMap.set(m.userId, m);
    }
  });
  const dedupedLeaderboard = Array.from(lbMap.values()).sort(
    (a, b) => b.progressScore - a.progressScore || quizRank(b.mcqScore) - quizRank(a.mcqScore)
  );

  const stageRank: Record<FunnelMember['stage'], number> = { invited: 0, accepted: 1, onboarded: 2, inProgress: 3, completed: 4 };
  const fmMap = new Map<string, FunnelMember>();
  funnelMembers.forEach((m) => {
    const prev = fmMap.get(m.userId);
    const better =
      !prev ||
      stageRank[m.stage] > stageRank[prev.stage] ||
      (stageRank[m.stage] === stageRank[prev.stage] &&
        (Number(m.isAligned) > Number(prev.isAligned) ||
          (m.isAligned === prev.isAligned && m.progressScore > prev.progressScore)));
    if (better) fmMap.set(m.userId, m);
  });
  const dedupedFunnelMembers = Array.from(fmMap.values());

  // Funnel stages are exclusive buckets, as the BE builds them per program: each
  // person sits in the furthest stage they reached in any program.
  const stageCount = (stage: FunnelMember['stage']) => dedupedFunnelMembers.filter((m) => m.stage === stage).length;
  const funnelCounts = {
    invited: pendingInvites + stageCount('invited'),
    accepted: stageCount('accepted'),
    onboarded: stageCount('onboarded'),
    inProgress: stageCount('inProgress'),
    completed: stageCount('completed'),
    membersAligned: dedupedFunnelMembers.filter((m) => m.stage === 'completed' && m.isAligned).length
  };
  const totalMembers = pendingInvites + dedupedFunnelMembers.length;
  const pct = (count: number) => (totalMembers > 0 ? (count / totalMembers) * 100 : 0);
  // Score scope = everyone past the invite stage (accepted / onboarded / in
  // progress / completed), the same definition the BE uses per program.
  const onboardedScope = dedupedFunnelMembers.filter((m) => m.stage !== 'invited').length;

  breakdown.sort((a, b) => Number(b.hasInsights) - Number(a.hasInsights) || b.valueAdded - a.valueAdded);

  const insights: InsightsV3 = {
    programId: ALL_PROGRAMS_ID,
    programName: ALL_PROGRAMS_NAME,
    currency,
    totalMembers,
    totalValueAdded: { totalAmount, totalHoursSaved: tenth(totalHoursSaved) },
    timeSpent: { totalHours: timeTotalHours, rows: timeRows },
    averageScores: {
      averageProgressScore,
      averageAssessmentScore,
      scope: { onboardedMembers: onboardedScope }
    },
    assessmentAnalysis: [],
    programFunnel: {
      invited: { count: funnelCounts.invited, percentage: pct(funnelCounts.invited) },
      accepted: { count: funnelCounts.accepted, percentage: pct(funnelCounts.accepted) },
      onboarded: { count: funnelCounts.onboarded, percentage: pct(funnelCounts.onboarded) },
      inProgress: { count: funnelCounts.inProgress, percentage: pct(funnelCounts.inProgress) },
      completed: { count: funnelCounts.completed, percentage: pct(funnelCounts.completed) },
      membersAligned: { count: funnelCounts.membersAligned, percentage: pct(funnelCounts.membersAligned) },
      members: dedupedFunnelMembers
    },
    leaderboard: dedupedLeaderboard
  };

  return { insights, breakdown };
};

/* ══════════════════════════════════════════
   MENTORSHIP CARDS
══════════════════════════════════════════ */

const CardMembersEngagement = ({ data, loading }: { data: MentorshipInsightsV3['membersEngagement'] | null; loading: boolean }) => {
  const stats = [
    { value: data?.messagesShared ?? 0, label: 'Messages shared', isRating: false },
    { value: data?.meetingsScheduled ?? 0, label: 'Meetings scheduled', isRating: false },
    { value: data?.averageRating ?? 0, label: 'Average rating', isRating: true }
  ];

  return (
    <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '20px', height: '100%', overflow: 'hidden' }}>
      <CardTitle title="Engagement" sub="Learner interactions count" tooltip="Program member interactions count" />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', minHeight: 0, overflow: 'hidden' }}>
        {stats.map(({ value, label, isRating }) => (
          <Box key={label} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', p: '10px', borderRadius: '5px', minHeight: 0, overflow: 'hidden' }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 36, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1, flexShrink: 0 }}>
              {loading ? <LoadingFigure /> : isRating ? value.toFixed(1) : value.toLocaleString('en-US')}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.2, textAlign: 'right', flex: '1 1 0', minWidth: 0 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const CardObjectiveAnalysis = ({ items, loading }: { items: MentorshipInsightsV3['objectiveAnalysis']; loading: boolean }) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%', overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}
        >
          Objective analysis
        </Typography>
        <InfoTip title="Member objectives broken down by type" />
      </Box>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, lineHeight: 1 }}>
        <span style={{ color: TEXT_TERTIARY }}>Selected as: </span>
        <span style={{ color: ACCENT }}>Learning Objective</span>
        <span style={{ color: TEXT_TERTIARY }}> | </span>
        <span style={{ color: ACCENT_DARK }}>Area of Expertise</span>
      </Typography>
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', overflowY: 'auto', minHeight: 0 }}>
      {loading && <LoadingRows />}
      {!loading && items.length === 0 && <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: TEXT_SECONDARY, p: '10px' }}>No objectives yet</Typography>}
      {!loading && items.map((item, i) => {
        const total = (item.learningObjectiveCount + item.areaOfExpertiseCount) || 1;
        const learningPct = (item.learningObjectiveCount / total) * 100;
        return (
          <Box key={i} sx={{ p: '10px', borderRadius: '5px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.3 }}>
                {item.objectiveText}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1, flexShrink: 0 }}>
                {item.totalCount}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: ACCENT, lineHeight: 1, flexShrink: 0, minWidth: 20, textAlign: 'right' }}>
                {item.learningObjectiveCount}
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', height: 5, overflow: 'hidden', borderRadius: '5px' }}>
                <Box sx={{ width: `${learningPct}%`, background: ACCENT, borderTopLeftRadius: '5px', borderBottomLeftRadius: '5px', flexShrink: 0 }} />
                <Box sx={{ flex: 1, background: ACCENT_DARK, borderTopRightRadius: '5px', borderBottomRightRadius: '5px' }} />
              </Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: ACCENT_DARK, lineHeight: 1, flexShrink: 0, minWidth: 20 }}>
                {item.areaOfExpertiseCount}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const MentorshipMembersDialog = ({
  open,
  onClose,
  members,
  title
}: {
  open: boolean;
  onClose: () => void;
  members: MentorshipFunnelMember[];
  title: string;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: '6px', backgroundImage: 'none' } }}
  >
    <Box sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '70vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: TEXT_PRIMARY, textTransform: 'uppercase', lineHeight: 1 }}>
          {title}
        </Typography>
        <CloseIcon onClick={onClose} sx={{ fontSize: 22, color: TEXT_TERTIARY, cursor: 'pointer', '&:hover': { color: TEXT_PRIMARY } }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
        {members.length === 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_TERTIARY, p: '10px' }}>
            No people yet
          </Typography>
        )}
        {members.map((m) => (
          <Box key={m.userId} sx={{ px: '8px', py: '10px', borderRadius: '6px', '&:hover': { background: BG_HOVER_ROW } }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1 }}>
              {m.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Dialog>
);

const CardMentorshipFunnel = ({
  funnel,
  mentorFunnel,
  loading
}: {
  funnel: MentorshipInsightsV3['programFunnel'] | null;
  mentorFunnel: MentorshipInsightsV3['mentorFunnel'] | null;
  loading: boolean;
}) => {
  const [viewRole, setViewRole] = useState<'mentees' | 'mentors'>('mentees');
  const [membersOpen, setMembersOpen] = useState(false);
  const w = useMemberWord();
  // The toggle keeps its 'mentees' / 'mentors' values; what it shows is the org's own plural word.
  const roleWord = (role: 'mentees' | 'mentors') => w(role === 'mentors' ? 'mentor' : 'mentee', { plural: true });

  const isMentors = viewRole === 'mentors';
  const activeFunnel = isMentors ? mentorFunnel : funnel;
  const members = activeFunnel?.members ?? [];
  const total = (isMentors ? mentorFunnel?.totalMentors : funnel?.totalMentees) ?? 0;
  const roleLabel = `Program ${roleWord(viewRole)}`;

  type MentorshipStageKey = 'invited' | 'accepted' | 'onboarded' | 'engaged' | 'objectivesInProgress';
  const stages: Array<{ label: string; key: MentorshipStageKey }> = [
    { label: 'Invited', key: 'invited' },
    { label: 'Accepted', key: 'accepted' },
    { label: 'Onboarded', key: 'onboarded' },
    { label: 'Engaged', key: 'engaged' },
    { label: 'At least one objective completed', key: 'objectivesInProgress' }
  ];

  return (
    <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '20px', height: '100%', overflow: 'hidden', position: 'relative' }}>
      {/* Mentees / Mentors toggle */}
      <Box sx={{ position: 'absolute', top: '19.5px', right: '20px', display: 'flex', overflow: 'hidden', borderRadius: '6px' }}>
        {(['mentees', 'mentors'] as const).map((role) => (
          <Box
            key={role}
            onClick={() => setViewRole(role)}
            sx={{
              px: '8px', py: '5px', cursor: 'pointer',
              background: viewRole === role ? '#444' : BORDER,
              '&:hover': { background: viewRole === role ? '#444' : '#333' }
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 10,
                fontWeight: viewRole === role ? 700 : 400,
                color: viewRole === role ? TEXT_PRIMARY : TEXT_SECONDARY,
                lineHeight: 1
              }}
            >
              {roleWord(role)}
            </Typography>
          </Box>
        ))}
      </Box>

      <CardTitle title="Program funnel" tooltip="A snapshot of your program members' onboarding" />

      {/* Header: total mentees */}
      <Box sx={{ background: BG_CARD_HIGHLIGHT, borderRadius: '5px', p: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 36, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}>
            {loading ? <LoadingFigure /> : total}
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_TERTIARY, lineHeight: 1 }}>
            {roleLabel}
          </Typography>
        </Box>
        <Typography onClick={() => setMembersOpen(true)} sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: ACCENT, cursor: 'pointer', lineHeight: 1, '&:hover': { textDecoration: 'underline' } }}>
          View
        </Typography>
      </Box>

      {/* Funnel stages */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minHeight: 0, overflow: 'hidden', justifyContent: 'center' }}>
        {stages.map((s) => (
          <StatBar key={s.key} label={s.label} count={activeFunnel?.[s.key]?.count ?? 0} percentage={activeFunnel?.[s.key]?.percentage ?? 0} loading={loading} />
        ))}
      </Box>

      {/* Footer: objectives completed */}
      <Box sx={{ background: BG_CARD_CONTRAST, borderRadius: '5px', p: '10px', display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 36, fontWeight: 600, color: ACCENT, lineHeight: 1 }}>
          {loading ? <LoadingFigure /> : activeFunnel?.objectivesCompleted?.count ?? 0}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: '5px' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1 }}>
            People with all objectives completed:
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: ACCENT, lineHeight: 1 }}>
            {loading ? <LoadingFigure /> : `${Math.round(activeFunnel?.objectivesCompleted?.percentage ?? 0)}%`}
          </Typography>
        </Box>
      </Box>

      <MentorshipMembersDialog open={membersOpen} onClose={() => setMembersOpen(false)} members={members} title={roleLabel} />
    </Box>
  );
};

const CardMentorshipLeaderboard = ({
  members,
  loading,
  title
}: {
  members: MentorshipInsightsV3['leaderboard'];
  loading: boolean;
  title: string;
}) => (
  <Box sx={{ background: BG_CARD, border: CARD_FRAME, borderRadius: '8px', p: '20px', display: 'flex', flexDirection: 'column', gap: '26px', height: '100%', overflow: 'hidden' }}>
    <CardTitle title={title} />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', p: '9px 8px', background: HEAD_FILL, borderBottom: `1px solid ${BORDER}`, borderRadius: '4px 4px 0 0' }}>
        <Typography sx={{ flex: 1, pl: '8px', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', lineHeight: 1 }}>Name</Typography>
        <Typography sx={{ width: 90, fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Messages</Typography>
        <Typography sx={{ width: 75, fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: TEXT_TERTIARY, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Meetings</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && <LoadingRows />}
        {!loading && members.length === 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: TEXT_SECONDARY, p: '10px' }}>
            No people yet
          </Typography>
        )}
        {!loading && members.map((m) => (
          <Box key={m.userId} sx={{ display: 'flex', gap: '15px', alignItems: 'center', px: '8px', py: '10px', borderRadius: '6px', '&:hover': { background: BG_HOVER_ROW } }}>
            <Typography sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', [MOBILE_SX]: { display: 'block', WebkitLineClamp: 'unset', overflow: 'visible' } }}>
              {m.name}
            </Typography>
            <Typography sx={{ width: 90, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
              {m.messages}
            </Typography>
            <Typography sx={{ width: 75, fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: TEXT_SECONDARY, textAlign: 'right', lineHeight: 1 }}>
              {m.meetings}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/* ══════════════════════════════════════════
   PROGRAM SELECTOR (all programs)
══════════════════════════════════════════ */
const ProgramSelector = ({
  currentId,
  currentName,
  programs,
  onSelect
}: {
  currentId: string;
  currentName: string;
  programs: Array<{ grpId: string; name: string }>;
  onSelect: (grpId: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const displayName =
    currentName ||
    programs.find((p) => p.grpId === currentId)?.name ||
    'Select program';

  const handleSelect = (grpId: string) => {
    setAnchorEl(null);
    if (grpId !== currentId) onSelect(grpId);
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          border: `1px solid ${ACCENT_LINE}`,
          borderRadius: '4px',
          background: ACCENT_TINT,
          pl: '12px',
          pr: '6px',
          py: '6px',
          '&:hover': { borderColor: ACCENT },
          cursor: 'pointer',
          minHeight: 30,
          // Phone: the filter takes the row, and a long program name wraps instead of running off it.
          [MOBILE_SX]: { flex: 1, minWidth: 0, minHeight: 40, justifyContent: 'space-between' }
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12.5,
            fontWeight: 600,
            color: TEXT_PRIMARY,
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { fontSize: 13, whiteSpace: 'normal', lineHeight: 1.3 }
          }}
        >
          {displayName}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: TEXT_PRIMARY }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            background: BG_CARD,
            border: `1px solid ${BORDER}`,
            borderRadius: '6px',
            mt: '4px',
            maxHeight: 360,
            minWidth: 280
          }
        }}
      >
        {programs.length === 0 && (
          <MenuItem disabled sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_TERTIARY }}>
            No programs available
          </MenuItem>
        )}
        {programs.map((g) => (
          <MenuItem
            key={g.grpId}
            onClick={() => handleSelect(g.grpId)}
            selected={g.grpId === currentId}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              fontWeight: g.grpId === ALL_PROGRAMS_ID ? 600 : 500,
              color: TEXT_PRIMARY,
              ...(g.grpId === ALL_PROGRAMS_ID ? { borderBottom: `1px solid ${BORDER}` } : {}),
              '&.Mui-selected': { background: BG_CARD_HIGHLIGHT, color: ACCENT },
              '&:hover': { background: BG_HOVER_ROW },
              [MOBILE_SX]: { whiteSpace: 'normal', minHeight: 44 }
            }}
          >
            {g.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const ProgramInsightsV3 = () => {
  const { orgId } = useProgramContext();
  const w = useMemberWord();
  // "Mentee leaderboard" in the org's own word: sentence case on the card, Title Case like the other CSV headings.
  const leaderboardTitle = `${w('mentee')} leaderboard`;
  const leaderboardCsvHeading = `${w('mentee')} Leaderboard`;
  const orgRealId = useAppSelector((s) => s.orgDetails.data?._id);
  const orgCurrency = useAppSelector((s) => s.orgDetails.data?.currency);
  const effectiveOrgId = orgRealId || orgId;
  const { data: adminPrograms = EMPTY_ADMIN_PROGRAMS, isLoading: programsLoading } = useAdminPrograms({
    orgId: effectiveOrgId,
    enabled: true
  });

  const allPrograms = useMemo(
    () => adminPrograms.filter((p) => p.grpId && p.name),
    [adminPrograms]
  );
  // The rollup only totals published programs; the selector still lists every
  // program so a draft can be opened on its own.
  const publishedPrograms = useMemo(() => allPrograms.filter((p) => isPublishedProgram(p.status)), [allPrograms]);

  // Land on the aggregated rollup by default; drilling into a program swaps this.
  const [selectedGrpId, setSelectedGrpId] = useState<string>(ALL_PROGRAMS_ID);
  const [masterclassData, setMasterclassData] = useState<InsightsV3 | null>(null);
  const [mentorshipData, setMentorshipData] = useState<MentorshipInsightsV3 | null>(null);
  const [programsBreakdown, setProgramsBreakdown] = useState<ProgramBreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);

  const isAllPrograms = selectedGrpId === ALL_PROGRAMS_ID;

  const selectedProgram = useMemo(
    () => allPrograms.find((p) => p.grpId === selectedGrpId),
    [allPrograms, selectedGrpId]
  );

  // The rollup renders through the masterclass layout (masterclass-weighted).
  const insightsVariant: InsightsVariant = useMemo(
    () => (isAllPrograms ? 'masterclass' : getInsightsVariant(selectedProgram?.type)),
    [isAllPrograms, selectedProgram]
  );

  const handleDownload = () => {
    const rows: string[][] = [];
    const add = (...cols: (string | number)[]) => rows.push(cols.map(String));
    const blank = () => rows.push([]);
    const heading = (title: string) => {
      blank();
      add(title);
      blank();
    };

    // ── Mentorship export ──
    if (insightsVariant === 'mentorship') {
      const d = mentorshipData;
      if (!d) return;

      add('Program', d.programName);
      blank();

      heading('Total Value Added');
      add('Total Amount', `${d.currency}${Math.round(d.totalValueAdded.totalAmount).toLocaleString()}`);
      add('Hours Saved', d.totalValueAdded.totalHoursSaved);

      heading('Time Spent');
      add('Total Hours', d.timeSpent.totalHours);
      blank();
      add('Channel', 'Hours', '%');
      const shares = sharesOf(d.timeSpent.rows);
      d.timeSpent.rows.forEach((r, i) => add(channelLabel(r.channel), r.hours, shares[i]));

      heading('Engagement');
      add('Messages Shared', d.membersEngagement.messagesShared);
      add('Meetings Scheduled', d.membersEngagement.meetingsScheduled);
      add('Average Rating', d.membersEngagement.averageRating);

      heading('Objective Analysis');
      add('Objective', 'Total', 'Learning Objective', 'Area of Expertise');
      d.objectiveAnalysis.forEach((o) =>
        add(o.objectiveText, o.totalCount, o.learningObjectiveCount, o.areaOfExpertiseCount)
      );

      heading('Program Funnel');
      add('Stage', 'Count', '%');
      const mStages: Array<[string, 'invited' | 'accepted' | 'onboarded' | 'engaged' | 'objectivesInProgress']> = [
        ['Invited', 'invited'], ['Accepted', 'accepted'], ['Onboarded', 'onboarded'],
        ['Engaged', 'engaged'], ['At Least One Objective Completed', 'objectivesInProgress']
      ];
      mStages.forEach(([label, key]) => {
        const s = d.programFunnel[key];
        add(label, s.count, `${Math.round(s.percentage)}%`);
      });
      add(
        'All Objectives Completed',
        d.programFunnel.objectivesCompleted.count,
        `${Math.round(d.programFunnel.objectivesCompleted.percentage)}%`
      );

      if (d.leaderboard.length > 0) {
        heading(leaderboardCsvHeading);
        add('Name', 'Messages', 'Meetings');
        d.leaderboard.forEach((m, i) => add(`${i + 1}. ${m.name}`, m.messages, m.meetings));
      }

      downloadCsv(rows, d.programName);
      return;
    }

    // ── Masterclass export ──
    const d = masterclassData;
    if (!d) return;

    add('Program', d.programName);
    blank();

    heading('Total Value Added');
    add('Total Amount', `${d.currency}${Math.round(d.totalValueAdded.totalAmount).toLocaleString()}`);
    add('Hours Saved', d.totalValueAdded.totalHoursSaved);

    heading('Time Spent');
    add('Total Hours', d.timeSpent.totalHours);
    blank();
    add('Channel', 'Hours', '%');
    const shares = sharesOf(d.timeSpent.rows);
    d.timeSpent.rows.forEach((r, i) => add(channelLabel(r.channel), r.hours, shares[i]));

    heading('Average Scores');
    add('Average Progress Score', `${d.averageScores.averageProgressScore}%`);
    add('Average Assessment Score', `${d.averageScores.averageAssessmentScore}%`);
    if (d.averageScores.scope) add('Onboarded People (scope)', d.averageScores.scope.onboardedMembers);

    heading('Program Funnel');
    add('Stage', 'Count', '%');
    const mcStages: Array<[string, 'invited' | 'accepted' | 'onboarded' | 'inProgress' | 'completed' | 'membersAligned']> = [
      ['Invited', 'invited'], ['Accepted', 'accepted'], ['Onboarded', 'onboarded'],
      ['In progress', 'inProgress'],
      ['Completed', 'completed'],
      ['People Aligned', 'membersAligned']
    ];
    mcStages.forEach(([label, key]) => {
      const s = d.programFunnel[key];
      add(label, s.count, `${Math.round(s.percentage)}%`);
    });

    if (d.assessmentAnalysis.length > 0) {
      heading('Assessment Analysis');
      add('Question', 'Avg Correct %', 'Sample Size');
      d.assessmentAnalysis.forEach((q) =>
        add(q.questionText, `${Math.round(q.averageCorrectPct)}%`, q.sampleSize ?? '')
      );
    }

    if (d.leaderboard.length > 0) {
      heading('Leaderboard');
      add('Name', 'Progress Score', 'Quiz Score');
      d.leaderboard.forEach((m, i) => add(`${i + 1}. ${m.name}`, `${m.progressScore}%`, scoreOrDash(m.mcqScore)));
    }

    if (isAllPrograms && programsBreakdown.length > 0) {
      heading('Programs');
      add('Program', 'Type', 'People', 'Value Added');
      programsBreakdown.forEach((p) =>
        p.unavailable
          ? add(p.name, p.typeLabel, 'n/a', p.unavailable === 'noAccess' ? 'No access' : 'Unavailable')
          : add(p.name, p.typeLabel, p.members, `${d.currency}${Math.round(p.valueAdded).toLocaleString()}`)
      );
    }

    downloadCsv(rows, d.programName);
  };

  const currency = orgCurrency || '$';

  // Single-program fetch (skipped for the aggregated rollup). Cancelled on
  // change so a slow response can't land under a different selection — e.g.
  // overwrite the All Programs rollup with one program's payload.
  useEffect(() => {
    if (isAllPrograms || !selectedGrpId || !insightsVariant) return undefined;
    let cancelled = false;
    setLoading(true);
    setMasterclassData(null);
    setMentorshipData(null);

    const apiCall =
      insightsVariant === 'mentorship'
        ? (API as any).getMentorshipInsightsV3(selectedGrpId)
        : (API as any).getProgramInsightsV3(selectedGrpId);

    apiCall
      .then((res: any) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        if (insightsVariant === 'mentorship') {
          setMentorshipData(payload as MentorshipInsightsV3);
        } else {
          setMasterclassData(payload as InsightsV3);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAllPrograms, selectedGrpId, insightsVariant]);

  // All-programs rollup: fetch every PUBLISHED program, a few at a time, and
  // aggregate client-side. Waits for the program list so an empty list never
  // paints a $0 rollup before the real one.
  useEffect(() => {
    if (!isAllPrograms || programsLoading) return undefined;
    let cancelled = false;
    setLoading(true);
    setMasterclassData(null);
    setMentorshipData(null);
    setProgramsBreakdown([]);

    mapWithConcurrency(publishedPrograms, ROLLUP_CONCURRENCY, async (p): Promise<AggregateResult> => {
      const variant = getInsightsVariant(p.type);
      const result: AggregateResult = {
        program: { grpId: p.grpId, name: p.name, typeLabel: p.typeLabel ?? '' },
        variant,
        masterclass: null,
        mentorship: null
      };
      try {
        if (variant === 'masterclass') {
          const res = await (API as any).getProgramInsightsV3(p.grpId);
          result.masterclass = (res?.data?.data ?? res?.data ?? null) as InsightsV3 | null;
        } else if (variant === 'mentorship') {
          const res = await (API as any).getMentorshipInsightsV3(p.grpId);
          result.mentorship = (res?.data?.data ?? res?.data ?? null) as MentorshipInsightsV3 | null;
        }
      } catch (e: any) {
        // A group admin can list org programs they can't read insights for
        // (401/403). Keep the program in the breakdown, flagged, and out of the
        // totals — never as a silent $0 row.
        const status = e?.response?.status;
        result.error = status === 401 || status === 403 ? 'noAccess' : 'failed';
      }
      return result;
    })
      .then((results) => {
        if (cancelled) return;
        const { insights, breakdown } = buildAllProgramsAggregate(results, currency);
        setMasterclassData(insights);
        setProgramsBreakdown(breakdown);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAllPrograms, programsLoading, publishedPrograms, currency]);

  const currentName = isAllPrograms
    ? ALL_PROGRAMS_NAME
    : insightsVariant === 'mentorship'
    ? mentorshipData?.programName ?? ''
    : masterclassData?.programName ?? '';

  const selectorPrograms = useMemo(
    () => [
      { grpId: ALL_PROGRAMS_ID, name: ALL_PROGRAMS_NAME },
      ...allPrograms.map((p) => ({ grpId: p.grpId, name: p.name }))
    ],
    [allPrograms]
  );

  // While the rollup waits on the program list, keep cards in their loading state.
  const effectiveLoading = isAllPrograms ? loading || programsLoading : loading;
  const hasData = insightsVariant === 'mentorship' ? Boolean(mentorshipData) : Boolean(masterclassData);

  return (
    <Box
      sx={{
        background: BG_PAGE,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        // Phone: the cards stack in one column and the app's content area scrolls them.
        [MOBILE_SX]: { height: 'auto', overflow: 'visible' }
      }}
    >
      {/* Subheader */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: '25px',
          py: '12px',
          gap: '10px',
          width: '100%',
          [MOBILE_SX]: { px: 0, pt: 0 }
        }}
      >
        {/* The program filter, just left of the download. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', [MOBILE_SX]: { flex: 1, minWidth: 0 } }}>
          <ProgramSelector
            currentId={selectedGrpId}
            currentName={currentName}
            programs={selectorPrograms}
            onSelect={setSelectedGrpId}
          />
          <DosenTooltip title={hasData ? 'Download CSV' : 'No data to export'}>
            <Box
              onClick={handleDownload}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${BORDER}`,
                borderRadius: '4px',
                px: '10px',
                py: '6px',
                cursor: hasData ? 'pointer' : 'not-allowed',
                background: BG_BUTTON_BASIC,
                opacity: hasData ? 1 : 0.4,
                '&:hover': hasData ? { background: '#2A2A2A' } : {},
                [MOBILE_SX]: { flexShrink: 0, width: 40, height: 40, p: 0 }
              }}
            >
              <DownloadIcon sx={{ fontSize: 18, color: TEXT_PRIMARY }} />
            </Box>
          </DosenTooltip>
        </Box>
      </Box>

      {/* Card area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          px: '25px',
          pb: '20px',
          display: 'flex',
          gap: '12px',
          minHeight: 0,
          // Phone: the three columns, and the cards in them, one under another at their own height.
          [MOBILE_SX]: {
            flexDirection: 'column',
            overflow: 'visible',
            px: 0,
            pb: 0,
            '& > div, & > div > div': { flex: '0 0 auto' }
          }
        }}
      >
        {/* ── No insights available for this program type ── */}
        {!insightsVariant && selectedGrpId && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: TEXT_TERTIARY }}>
              Insights not available for this program type
            </Typography>
          </Box>
        )}

        {/* ── Mentorship layout ── */}
        {insightsVariant === 'mentorship' && (
          <>
            {/* Left */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
              <Box sx={{ flex: '205 1 0', minHeight: 0 }}>
                <CardTotalValueAdded totalAmount={mentorshipData?.totalValueAdded?.totalAmount ?? 0} hoursSaved={mentorshipData?.totalValueAdded?.totalHoursSaved ?? 0} currency={currency} loading={loading} />
              </Box>
              <Box sx={{ flex: '312.5 1 0', minHeight: 0 }}>
                <CardTimeSpent data={mentorshipData?.timeSpent ?? null} loading={loading} />
              </Box>
              <Box sx={{ flex: '280 1 0', minHeight: 0 }}>
                <CardMembersEngagement data={mentorshipData?.membersEngagement ?? null} loading={loading} />
              </Box>
            </Box>
            {/* Middle */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
              <Box sx={{ flex: '312.5 1 0', minHeight: 0 }}>
                <CardObjectiveAnalysis items={mentorshipData?.objectiveAnalysis ?? []} loading={loading} />
              </Box>
              <Box sx={{ flex: '449.5 1 0', minHeight: 0 }}>
                <CardMentorshipFunnel funnel={mentorshipData?.programFunnel ?? null} mentorFunnel={mentorshipData?.mentorFunnel ?? null} loading={loading} />
              </Box>
            </Box>
            {/* Right */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <CardMentorshipLeaderboard
                members={mentorshipData?.leaderboard ?? []}
                loading={loading}
                title={leaderboardTitle}
              />
            </Box>
          </>
        )}

        {/* ── Masterclass layout (also the aggregated All Programs rollup) ── */}
        {insightsVariant === 'masterclass' && (
          <>
            {/* Left */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
              <Box sx={{ flex: '205 1 0', minHeight: 0 }}>
                <CardTotalValueAdded totalAmount={masterclassData?.totalValueAdded?.totalAmount ?? 0} hoursSaved={masterclassData?.totalValueAdded?.totalHoursSaved ?? 0} currency={currency} loading={effectiveLoading} />
              </Box>
              <Box sx={{ flex: '312.5 1 0', minHeight: 0 }}>
                <CardTimeSpent data={masterclassData?.timeSpent ?? null} loading={effectiveLoading} />
              </Box>
              <Box sx={{ flex: '280 1 0', minHeight: 0 }}>
                <CardAverageScores data={masterclassData?.averageScores ?? null} loading={effectiveLoading} />
              </Box>
            </Box>
            {/* Middle */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
              <Box sx={{ flex: '312.5 1 0', minHeight: 0 }}>
                {isAllPrograms ? (
                  <CardProgramsBreakdown rows={programsBreakdown} currency={currency} loading={effectiveLoading} onSelect={setSelectedGrpId} />
                ) : (
                  <CardAssessmentAnalysis items={masterclassData?.assessmentAnalysis ?? []} loading={effectiveLoading} />
                )}
              </Box>
              <Box sx={{ flex: '449.5 1 0', minHeight: 0 }}>
                <CardProgramFunnel funnel={masterclassData?.programFunnel ?? null} totalMembers={masterclassData?.totalMembers ?? 0} loading={effectiveLoading} />
              </Box>
            </Box>
            {/* Right */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <CardMembersLeaderboard members={masterclassData?.leaderboard ?? []} loading={effectiveLoading} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProgramInsightsV3;
